import { FC, MutableRefObject, useMemo, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { confirmDialog } from 'primereact/confirmdialog';
import { encode } from 'entities';
import { UserPlus } from 'lucide-react';
import { createUsers, getAllUserRoles, updateUser } from '../../../graphQLOperations/Users';
import { UsersCreateUsersMutation } from '../../../graphQLOperations/__generated__/UsersCreateUsersMutation.graphql';
import { UsersUpdateUsersMutation } from '../../../graphQLOperations/__generated__/UsersUpdateUsersMutation.graphql';
import { UsersAllRolesQuery } from '../../../graphQLOperations/__generated__/UsersAllRolesQuery.graphql';
import { isValidEmail } from '../../../businessLogicService/emailValidator';
import { ErrorMessages, WizardUser } from './wizardTypes';
import { CompleteUser } from '../../../custom-hooks/useGetUsersBySite';
import { Role } from '../../../models/Role';
import { Site } from '../../../models/Site';
import User, { Roles } from '../../../models/User';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectUser, setUser } from '../../../redux/reducers/userSlice';

interface Props {
  visible: boolean;
  onHide: () => void;
  siteId: string;
  siteName: string;
  onCreated?: (teacher: WizardUser) => void;
  toast: MutableRefObject<Toast | null>;
  teacher?: CompleteUser;
  siteOptions?: Site[];
  onSaved?: () => void;
}

const emptyValues = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  role: null as Role | null,
  sites: [] as Site[],
};

const ClassWizardNewTeacherDialog: FC<Props> = ({
  visible,
  onHide,
  siteId,
  siteName,
  onCreated,
  onSaved,
  toast,
  teacher,
  siteOptions,
}) => {
  const isEdit = Boolean(teacher);
  const usersPageMode = Boolean(siteOptions);
  const userRedux = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const [values, setValues] = useState(() =>
    teacher
      ? {
          firstName: teacher.firstName,
          lastName: teacher.lastName,
          emailAddress: teacher.emailAddress,
          role: teacher.role?.[0] ? { id: teacher.role[0].id, name: teacher.role[0].name } : null,
          sites: teacher.site ?? [],
        }
      : emptyValues,
  );
  const [submitted, setSubmitted] = useState(false);
  const [createTeacherMutation, creating] = useMutation<UsersCreateUsersMutation>(createUsers);
  const [updateTeacherMutation, updating] = useMutation<UsersUpdateUsersMutation>(updateUser);
  const loading = creating || updating;

  const allRolesData = useLazyLoadQuery<UsersAllRolesQuery>(getAllUserRoles, {});

  const viewerRoleNames = useMemo(() => userRedux?.roles?.map(role => role.name) ?? [], [userRedux]);
  const isTeacherViewer =
    viewerRoleNames.includes(Roles.TEACHER) &&
    !viewerRoleNames.includes(Roles.NETWORK_ADMIN) &&
    !viewerRoleNames.includes(Roles.SITE_ADMIN);

  const roleOptions = useMemo(() => {
    if (!usersPageMode) return [];
    const roles = (allRolesData?.allRoles?.nodes ?? []).map(role => ({
      id: role?.id ?? '',
      name: role?.name ? `${role.name.charAt(0).toUpperCase()}${role.name.slice(1)}` : '',
    })) as Role[];
    const networkAdminRole = roles.find(role => role.name.toLowerCase() === Roles.NETWORK_ADMIN);
    const siteAdminRole = roles.find(role => role.name.toLowerCase() === Roles.SITE_ADMIN);
    const teacherRole = roles.find(role => role.name.toLowerCase() === Roles.TEACHER);

    if (viewerRoleNames.includes(Roles.NETWORK_ADMIN)) {
      return [networkAdminRole, siteAdminRole, teacherRole].filter(Boolean) as Role[];
    }
    if (viewerRoleNames.includes(Roles.SITE_ADMIN)) {
      return [siteAdminRole, teacherRole].filter(Boolean) as Role[];
    }
    return [];
  }, [allRolesData, usersPageMode, viewerRoleNames]);

  const sitesDisabled = usersPageMode && values.role?.name?.toLowerCase() === Roles.NETWORK_ADMIN;

  const errors: ErrorMessages = {
    firstName: values.firstName.trim() ? '' : 'First name is required.',
    lastName: values.lastName.trim() ? '' : 'Last name is required.',
    emailAddress: !values.emailAddress.trim()
      ? 'Email address is required.'
      : !isValidEmail(values.emailAddress)
      ? 'Invalid email address.'
      : '',
    role: usersPageMode && !values.role ? 'User role is required.' : '',
    sites: usersPageMode && !values.sites.length ? 'Site is required.' : '',
  };
  const isValid = Object.values(errors).every(message => !message);

  const close = () => {
    setValues(emptyValues);
    setSubmitted(false);
    onHide();
  };

  const showError = (message?: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail:
        message ?? 'There was an error. Usually this happens when an account with that email address already exists.',
    });
  };

  const handleRoleChange = (role: Role) => {
    setValues(prev => ({
      ...prev,
      role,
      sites: role?.name?.toLowerCase() === Roles.NETWORK_ADMIN ? siteOptions ?? [] : prev.sites,
    }));
  };

  const performSave = () => {
    if (isEdit && teacher) {
      updateTeacherMutation({
        variables: {
          input: {
            email: values.emailAddress,
            firstName: values.firstName,
            lastName: values.lastName,
            userId: teacher.id,
            roleIds: [usersPageMode ? values.role?.id ?? '' : 'RoleNode:3'],
            siteIds: usersPageMode ? values.sites.map(site => site.id) : [siteId],
            isActive: true,
          },
        },
        onCompleted: () => {
          if (`UserNode:${userRedux?.id}` === teacher.id) {
            dispatch(setUser({ ...userRedux, name: `${values.firstName} ${values.lastName}` } as User));
          }
          toast.current?.show({
            severity: 'success',
            summary: 'Teacher or Admin Updated',
            detail: `${encode(values.firstName)} ${encode(values.lastName)} has been updated`,
          });
          onSaved?.();
          close();
        },
        onError: () => showError(),
      });
      return;
    }

    createTeacherMutation({
      variables: {
        input: {
          inputs: [
            {
              email: values.emailAddress,
              firstName: values.firstName,
              lastName: values.lastName,
              siteIds: usersPageMode ? values.sites.map(site => site.id) : [siteId],
              connection: 'username-passs',
              roleId: usersPageMode ? values.role?.id ?? '' : 'RoleNode:3',
            },
          ],
        },
      },
      onCompleted: data => {
        const created = data?.createUsers?.createUserResult?.createdUsers?.[0];
        if (!created) {
          showError(data?.createUsers?.errors?.[0]?.message);
          return;
        }
        toast.current?.show({
          severity: 'success',
          summary: 'Teacher or Admin Created',
          detail: `${created.firstName} ${created.lastName} was created and added.`,
        });
        onCreated?.({ id: created.id, firstName: created.firstName ?? '', lastName: created.lastName ?? '' });
        onSaved?.();
        close();
      },
      onError: () => showError(),
    });
  };

  const submit = () => {
    setSubmitted(true);
    if (!isValid || loading) return;

    if (!usersPageMode) {
      performSave();
      return;
    }

    const { firstName, lastName } = values;
    confirmDialog({
      message: (
        <>
          {`Are you sure you want to ${isEdit ? 'update' : 'create'} the following teacher or admin account?`}
          <ul>
            {encode(firstName)} {encode(lastName)}
          </ul>
        </>
      ),
      header: isEdit ? 'Confirm Updating Teacher or Admin' : 'Confirm Creating Teacher or Admin',
      icon: 'nc_icon-nc_warning icon-md',
      acceptLabel: isEdit ? 'Update Teacher or Admin' : 'Create Teacher or Admin',
      acceptIcon: isEdit ? 'nc_icon-nc_save icon-sm' : 'nc_icon-nc_add icon-sm',
      acceptClassName: 'p-button-primary',
      rejectLabel: 'Cancel',
      rejectClassName: 'p-button-link',
      rejectIcon: 'nc_icon-nc_close icon-sm',
      defaultFocus: 'accept',
      accept: performSave,
    });
  };

  const header = (
    <div className="flex align-items-start gap-md">
      <span className="nc_wizard-header-icon">
        <UserPlus size={17} aria-hidden />
      </span>
      <div className="flex flex-column">
        <h2 className="text-xl font-semibold line-height-1 m-0 tw-not-italic">
          {isEdit ? 'Edit teacher or admin' : usersPageMode ? 'New teacher or admin' : 'New teacher'}
        </h2>
        {!isEdit && (
          <span className="text-color-secondary text-sm tw-leading-5 mt-1 tw-font-normal">
            New users will receive an email letting them know their account has been created and how to log in.
          </span>
        )}
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-content-between align-items-center gap-md w-full">
      <Button label="Cancel" outlined rounded onClick={close} disabled={loading} />
      <Button label="Save" icon="nc_icon-nc_save icon-sm" rounded onClick={submit} loading={loading} />
    </div>
  );

  return (
    <Dialog
      header={header}
      footer={footer}
      visible={visible}
      onHide={close}
      className="nc_class-wizard"
      style={{ width: '32.5rem' }}
      dismissableMask={false}
      ariaCloseIconLabel="Close"
    >
      <div className="flex flex-column gap-md py-2">
        <div className="grid">
          <div className="col-6 flex flex-column gap-xs">
            <label htmlFor="wizardTeacherFirstName" className="font-semibold">
              First name <span className="nc_required-mark">*</span>
            </label>
            <InputText
              id="wizardTeacherFirstName"
              value={values.firstName}
              onChange={event => setValues(prev => ({ ...prev, firstName: event.target.value }))}
            />
            {submitted && errors.firstName && <small className="p-error">{errors.firstName}</small>}
          </div>
          <div className="col-6 flex flex-column gap-xs">
            <label htmlFor="wizardTeacherLastName" className="font-semibold">
              Last name <span className="nc_required-mark">*</span>
            </label>
            <InputText
              id="wizardTeacherLastName"
              value={values.lastName}
              onChange={event => setValues(prev => ({ ...prev, lastName: event.target.value }))}
            />
            {submitted && errors.lastName && <small className="p-error">{errors.lastName}</small>}
          </div>
        </div>

        <div className="flex flex-column gap-xs">
          <label htmlFor="wizardTeacherEmail" className="font-semibold">
            Email address <span className="nc_required-mark">*</span>
          </label>
          <InputText
            id="wizardTeacherEmail"
            value={values.emailAddress}
            onChange={event => setValues(prev => ({ ...prev, emailAddress: event.target.value }))}
          />
          {submitted && errors.emailAddress && <small className="p-error">{errors.emailAddress}</small>}
        </div>

        {usersPageMode && (
          <div className="flex flex-column gap-xs">
            <label htmlFor="wizardTeacherRole" className="font-semibold">
              User role <span className="nc_required-mark">*</span>
            </label>
            <Dropdown
              inputId="wizardTeacherRole"
              value={values.role}
              options={roleOptions}
              optionLabel="name"
              dataKey="id"
              onChange={event => handleRoleChange(event.value as Role)}
              placeholder="Select role"
              className="w-full"
              disabled={isTeacherViewer && isEdit}
            />
            {submitted && errors.role && <small className="p-error">{errors.role}</small>}
          </div>
        )}

        <div className="flex flex-column gap-xs">
          <label htmlFor="wizardTeacherSite" className="font-semibold">
            {usersPageMode ? 'Site(s)' : 'Site'} <span className="nc_required-mark">*</span>
          </label>
          {usersPageMode ? (
            <MultiSelect
              inputId="wizardTeacherSite"
              value={values.sites}
              options={siteOptions}
              optionLabel="name"
              dataKey="id"
              onChange={(event: MultiSelectChangeEvent) =>
                setValues(prev => ({ ...prev, sites: event.value as Site[] }))
              }
              placeholder="Select site(s)"
              className="w-full"
              disabled={sitesDisabled}
            />
          ) : (
            <Dropdown
              inputId="wizardTeacherSite"
              value={siteId}
              options={[{ id: siteId, name: siteName }]}
              optionLabel="name"
              optionValue="id"
              disabled
              className="w-full"
            />
          )}
          {submitted && errors.sites && <small className="p-error">{errors.sites}</small>}
        </div>

        <span className="text-color-secondary text-sm">
          <span className="nc_required-mark">*</span> Required fields
        </span>
      </div>
    </Dialog>
  );
};

export default ClassWizardNewTeacherDialog;
